/**
 * Cross-surface, remount-surviving guard for conversation ids confirmed gone (404/410).
 *
 * Why this exists: a conversation can be loaded from more than one independent code path -
 * the hosted-thread load effect (chat-pane.tsx -> chat-pane.handlers.ts `handleSelectConversation`)
 * and the legacy sessionStorage restore-on-mount hook (chat-pane.custom-hooks.ts
 * `useConversationHistoryLoader`). Each ChatPane surface (agent panel, editor tab) also has its
 * own per-instance `failedHostedLoadConversationIdRef`, which resets on every remount.
 *
 * Without a module-level (not component-level) record of "this id is confirmed gone", switching
 * chat tabs/threads repeatedly (which remounts surfaces) or having multiple surfaces reference the
 * same stale id via sessionStorage lets every remount/path re-attempt a fetch that is guaranteed to
 * 404 again - the production symptom was a barrage of repeated 404s for the same one or two
 * conversation ids from both call sites. This module is the single shared source of truth so any
 * path that discovers a conversation is gone stops every other path from retrying it too.
 */

const knownGoneConversationIds = new Set<string>()

/** True when this conversation id already 404/410'd in this session - callers should not refetch it. */
export function isKnownGoneConversationId(id: string | null | undefined): boolean {
  if (!id) return false
  return knownGoneConversationIds.has(id)
}

/** Record that a conversation id is confirmed gone (404/410) so no path retries it this session. */
export function markConversationIdGone(id: string | null | undefined): void {
  if (!id) return
  knownGoneConversationIds.add(id)
}

/** True for HTTP statuses that mean "this conversation will never load" (vs. a transient failure). */
export function isPermanentlyGoneStatus(status: number | undefined): boolean {
  return status === 404 || status === 410
}
